/**
 * 启动时把图片和音效拉进浏览器 HTTP 缓存。
 * 字体、副本音效不在范围内。小于内联阈值的图片是 data URL，不需要再请求。
 */

const IMAGE_CONTEXT = require.context(
  '@/assets/images',
  true,
  /\.(png|jpe?g|gif|webp)$/i
)
const SOUND_CONTEXT = require.context(
  '@/assets/sounds',
  false,
  /\.wav$/i
)

const CONCURRENCY = 6

function collectAssetUrls() {
  const urls = []
  ;[IMAGE_CONTEXT, SOUND_CONTEXT].forEach(context => {
    context.keys().forEach(key => {
      if (key.includes('副本')) return
      const url = context(key)
      if (typeof url !== 'string' || !url || url.startsWith('data:')) return
      urls.push(new URL(url, document.baseURI).href)
    })
  })
  return [...new Set(urls)]
}

function readContentLength(response) {
  const value = Number(response.headers.get('Content-Length'))
  return Number.isFinite(value) && value > 0 ? value : 0
}

async function readBody(response, onChunk) {
  const total = readContentLength(response)
  if (!response.body) {
    const blob = await response.blob()
    onChunk(blob.size, total || blob.size)
    return
  }

  const reader = response.body.getReader()
  let received = 0
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    received += value.byteLength
    onChunk(received, total)
  }
  onChunk(received, total || received)
}

async function downloadOnce(url, onChunk) {
  const response = await fetch(url, { cache: 'default' })
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  await readBody(response, onChunk)
}

async function downloadWithRetry(url, onChunk) {
  try {
    await downloadOnce(url, onChunk)
  } catch (firstError) {
    onChunk(0, 0)
    await downloadOnce(url, onChunk)
  }
}

function calcRatio(states) {
  if (!states.length) return 1

  const known = states.filter(item => item.total > 0)
  const unknown = states.filter(item => item.total <= 0)
  if (known.length === states.length) {
    const total = known.reduce((sum, item) => sum + item.total, 0)
    const received = known.reduce((sum, item) => sum + Math.min(item.received, item.total), 0)
    return total > 0 ? received / total : 1
  }

  const knownRatio = known.length
    ? known.reduce((sum, item) => sum + Math.min(item.received, item.total) / item.total, 0) / known.length
    : 0
  const unknownRatio = unknown.length
    ? unknown.filter(item => item.done).length / unknown.length
    : 1
  return (knownRatio * known.length + unknownRatio * unknown.length) / states.length
}

/**
 * @param {(progress: { percent: number, loaded: number, total: number, failed: number }) => void} onProgress
 * @returns {Promise<{ total: number, failed: number }>}
 */
export function preloadAssets(onProgress) {
  const urls = collectAssetUrls()
  const states = urls.map(() => ({
    received: 0,
    total: 0,
    done: false,
    failed: false
  }))

  const emit = () => {
    if (typeof onProgress !== 'function') return
    onProgress({
      percent: Math.min(100, Math.round(calcRatio(states) * 100)),
      loaded: states.filter(item => item.done).length,
      total: states.length,
      failed: states.filter(item => item.failed).length
    })
  }

  emit()
  if (!urls.length) {
    return Promise.resolve({ total: 0, failed: 0 })
  }

  let cursor = 0
  const worker = async () => {
    while (cursor < urls.length) {
      const index = cursor
      cursor += 1
      const state = states[index]
      try {
        await downloadWithRetry(urls[index], (received, total) => {
          state.received = received
          state.total = total
          emit()
        })
        state.done = true
      } catch (downloadError) {
        state.done = true
        state.failed = true
      }
      emit()
    }
  }

  const workers = Array.from(
    { length: Math.min(CONCURRENCY, urls.length) },
    () => worker()
  )
  return Promise.all(workers).then(() => ({
    total: urls.length,
    failed: states.filter(item => item.failed).length
  }))
}
